import {
  useMutation,
  useQuery,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";
import featureRequestsClient, {
  ALL_REQUESTS_QUERY_KEY,
  CreateFeatureRequestInput,
  CreateProductFeatureInput,
  FEATURE_REQUESTS_QUERY_PREFIX,
  FeatureRequestStatus,
  NotifyRequestsInput,
  PRODUCT_FEATURES_QUERY_KEY,
  UpdateFeatureRequestInput,
  UpdateProductFeatureInput,
} from "../../../api/featureRequests";

// Every write can change feature counts and request statuses, so both lists
// (they share the prefix) are refreshed together.
const invalidateAll = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: FEATURE_REQUESTS_QUERY_PREFIX });

const useInvalidatingMutation = <TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => invalidateAll(queryClient),
  });
};

export const useProductFeaturesQuery = () =>
  useQuery({
    queryKey: PRODUCT_FEATURES_QUERY_KEY,
    queryFn: () => featureRequestsClient.listFeatures(),
    staleTime: 60_000,
  });

export const useFeatureRequestsQuery = () =>
  useQuery({
    queryKey: ALL_REQUESTS_QUERY_KEY,
    queryFn: () => featureRequestsClient.listRequests(),
    staleTime: 60_000,
  });

export const useCreateFeatureMutation = () =>
  useInvalidatingMutation((input: CreateProductFeatureInput) =>
    featureRequestsClient.createFeature(input),
  );

export const useUpdateFeatureMutation = () =>
  useInvalidatingMutation(
    ({ id, input }: { id: string; input: UpdateProductFeatureInput }) =>
      featureRequestsClient.updateFeature(id, input),
  );

export const useDeleteFeatureMutation = () =>
  useInvalidatingMutation((id: string) =>
    featureRequestsClient.deleteFeature(id),
  );

export const useMergeFeatureMutation = () =>
  useInvalidatingMutation(({ id, intoId }: { id: string; intoId: string }) =>
    featureRequestsClient.mergeFeature(id, intoId),
  );

export const useDeployFeatureMutation = () =>
  useInvalidatingMutation(({ id, version }: { id: string; version: string }) =>
    featureRequestsClient.deployFeature(id, version),
  );

export const useUndeployFeatureMutation = () =>
  useInvalidatingMutation((id: string) =>
    featureRequestsClient.undeployFeature(id),
  );

export const useCreateRequestMutation = () =>
  useInvalidatingMutation((input: CreateFeatureRequestInput) =>
    featureRequestsClient.createRequest(input),
  );

export const useUpdateRequestMutation = () =>
  useInvalidatingMutation(
    ({ id, input }: { id: string; input: UpdateFeatureRequestInput }) =>
      featureRequestsClient.updateRequest(id, input),
  );

export const useSetRequestsStatusMutation = () =>
  useInvalidatingMutation(
    ({ ids, status }: { ids: string[]; status: FeatureRequestStatus }) =>
      featureRequestsClient.setRequestsStatus(ids, status),
  );

export const useDeleteRequestsMutation = () =>
  useInvalidatingMutation((ids: string[]) =>
    featureRequestsClient.deleteRequests(ids),
  );

// A dry run changes nothing, so only a real send refreshes the lists.
export const useNotifyRequestsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: NotifyRequestsInput) =>
      featureRequestsClient.notifyRequests(input),
    onSuccess: (result) => {
      if (!result.dryRun) invalidateAll(queryClient);
    },
  });
};
